import LegalLayout from "@/components/LegalLayout";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { SEO_ROUTES } from "../lib/seo";

import modelServiceTerms from '@/lib/model-service-terms.json';

const SECTIONS = [
  ...modelServiceTerms,
  {heading: "Mount installation, inspection & accuracy", body: ["ForgedOps mounts are mechanical mounting accessories, not a guarantee of survey, positioning, elevation, layout, grading or machine-control accuracy. These responsibilities apply to all ForgedOps base-station and rover mounts, as appropriate to each product’s intended use.", "The purchaser and operator are responsible for compatible equipment, a sound support and footing where applicable, secure installation, inspection and maintenance, correct receiver settings and antenna height, site localization/calibration, and verification against independent known control before work and after movement, impact or any disturbance. Stop use if the assembly is damaged, loose or unstable. Do not use mounts as steps, lifting points, tie-downs or fall-protection anchors.", "To the extent permitted by law, ForgedOps is not responsible for errors or loss caused by improper installation, inadequate support, incorrect settings or control data, failure to verify accuracy, abuse, impact, overloading, improper maintenance, alteration, or continued use of a damaged or unstable assembly. The Product Purchase Terms describe commercial economic-loss limitations. These provisions do not remove stated product-defect remedies or exclude liability for fraud, gross negligence, willful misconduct, personal injury, physical damage to other property, or rights and liability that cannot lawfully be limited."]},
  {heading: "Physical Product Purchases", body: [<>BASEPOINT purchases are also governed by our <a href="/shop/policies">Shipping, Returns &amp; One-Year Limited Warranty policies</a>. Those Product Purchase Terms, including GNSS setup responsibilities and commercial loss limitations, are incorporated into physical-product purchases and control over these general website terms for product shipping, returns, warranty, and use. Website availability disclaimers do not remove product warranty coverage or statutory consumer rights.</>]},
  {
    heading: "Acceptance of Terms",
    body: [
      "These Terms of Service govern your access to and use of the ForgedOps website, proprietary operational platforms, white-labeled deployments, training programs, and related services (collectively, the “ForgedOps Services”) operated by ForgedOps LLC (“ForgedOps,” “we,” “our,” “us”).",
      "By accessing this site, requesting a consultation, engaging ForgedOps for operational consulting, deploying a ForgedOps platform, or using any branded portal or system we deliver, you agree to be bound by these Terms. If you do not agree, do not use the ForgedOps Services.",
    ],
  },
  {
    heading: "Proprietary Systems & Intellectual Property",
    body: [
      "ForgedOps designs, develops, and deploys proprietary operational systems and infrastructure built from real-world heavy civil and infrastructure operations experience. All ForgedOps systems, platforms, workflows, training content, branded deployments, and supporting materials are protected as proprietary intellectual property.",
      "Without limitation, the following are owned by ForgedOps LLC and may not be copied, redistributed, reverse-engineered, or repurposed without written authorization:",
    ],
    bullets: [
      "Proprietary operational platforms and field workflow engines",
      "Proprietary operational intelligence systems and dashboards",
      "Custom proprietary workforce platforms and onboarding kits",
      "White-labeled deployments and customer-branded operational ecosystems",
      "Proprietary training curricula, OSHA outreach materials, and field documentation templates",
      "Operational reporting structures, approval-chain logic, and field sign-off architectures",
      "Bilingual workflow content, terminology libraries, and translation systems",
      "Brand assets including the ForgedOps name, marks, lockups, and visual identity",
    ],
  },
  {
    heading: "Limited License to Use the Site",
    body: [
      "Subject to these Terms, ForgedOps grants you a limited, revocable, non-exclusive, non-transferable license to access and view this website for the purpose of evaluating our services. No other rights are granted by implication, estoppel, or otherwise.",
      "You may not scrape, mirror, frame, or otherwise reproduce any portion of this site or its proprietary content for commercial purposes without prior written consent.",
    ],
  },
  {
    heading: "Engagement & Deployments",
    body: [
      "Specific consulting engagements, training programs, and platform deployments are governed by a separately executed Master Services Agreement, Statement of Work, or platform deployment agreement (“Engagement Agreement”). The Engagement Agreement controls in the event of conflict with these Terms.",
      "ForgedOps platform deployments are typically delivered as white-labeled, customer-branded operational systems. Customer-specific configurations, brand application, and workflow customizations remain subject to the licensing and ownership terms of the applicable Engagement Agreement.",
    ],
  },
  {
    heading: "Customer Data & Confidentiality",
    body: [
      "ForgedOps treats customer field data, project information, crew records, safety documentation, and operational reporting captured through our platforms as confidential, in accordance with the applicable Engagement Agreement and our Privacy Policy.",
      "ForgedOps does not sell customer operational data. Aggregated, anonymized operational benchmarks may be used internally to improve platform capabilities and field-tested workflows.",
    ],
  },
  {
    heading: "Acceptable Use",
    body: [
      "You agree not to use the ForgedOps Services to: (a) violate applicable laws or regulations; (b) infringe ForgedOps or any third-party intellectual property; (c) disrupt platform operations or attempt to gain unauthorized access; (d) introduce malicious code; or (e) misrepresent your identity, role, or authority when requesting consultations or accessing branded portals.",
    ],
  },
  {
    heading: "Disclaimers",
    body: [
      "The ForgedOps website and informational content are provided “as is.” ForgedOps does not warrant that the site will be uninterrupted, error-free, or available at all times. Operational outcomes from any consulting, training, or platform engagement depend on field conditions, customer adoption, and execution.",
      "Nothing on this website constitutes legal, safety, regulatory, or engineering advice. OSHA, DOT, and FAA compliance remain the responsibility of the operating contractor.",
    ],
  },
  {
    heading: "Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, ForgedOps LLC, its officers, employees, and contractors shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the ForgedOps website, except as expressly set forth in an applicable Engagement Agreement.",
    ],
  },
  {
    heading: "Modifications",
    body: [
      "ForgedOps may revise these Terms from time to time. Material changes will be reflected in the “Last updated” date above. Continued use of the ForgedOps Services after changes constitutes acceptance.",
    ],
  },
  {
    heading: "Governing Law",
    body: [
      "These Terms are governed by the laws of the State of Florida, without regard to conflict-of-law principles. Disputes shall be brought exclusively in the state or federal courts located in the State of Florida, unless otherwise agreed in writing.",
    ],
  },
  {
    heading: "Contact",
    body: [
      "Questions about these Terms or proprietary licensing inquiries can be directed to our operations team via the Contact page.",
    ],
  },
];

export default function Terms() {
  useDocumentMeta(SEO_ROUTES["/terms"]);
  return (
    <LegalLayout
      testid="terms-page"
      kicker="Legal · Operational"
      title={
        <>
          Terms of <span className="metallic-text">Service</span>
        </>
      }
      lastUpdated="November 2, 2026"
      intro="ForgedOps LLC develops proprietary operational systems, white-labeled platforms, and field-tested infrastructure for heavy civil and infrastructure contractors. These Terms govern your access to the ForgedOps website and services."
      sections={SECTIONS}
    />
  );
}

